/** Which of a project's milestones a piece of work goes towards.
 *
 * Only the chosen project's milestones are offered — the API refuses any other — and the
 * field is disabled until a project is picked. Linking shows progress on the milestone;
 * it never completes it (Brian, 2026-09-29).
 */

import { Field, inputClass } from '@/components/ui'
import { formatDate } from '@/lib/domain'
import { useMilestones } from '@/features/delivery/useDelivery'

export function MilestoneField({
  projectId,
  value,
  onChange,
}: {
  projectId: string
  value: string
  onChange: (value: string) => void
}) {
  const { data, isPending } = useMilestones(projectId)
  const milestones = projectId ? (data ?? []) : []
  // A link to a milestone since deleted: say so rather than showing "None" while the
  // item still carries it. The server clears these, so this is a brief state at most.
  const missing = Boolean(value) && !isPending && !milestones.some((m) => m.id === value)

  return (
    <Field
      label="Milestone"
      hint={
        !projectId
          ? 'Pick a project first.'
          : milestones.length === 0 && !isPending
            ? 'This project has no milestones yet.'
            : 'Counts this work towards the milestone’s progress.'
      }
    >
      <select
        className={inputClass}
        value={value}
        disabled={!projectId || (milestones.length === 0 && !value)}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">None</option>
        {missing ? <option value={value}>No longer available</option> : null}
        {milestones.map((milestone) => (
          <option key={milestone.id} value={milestone.id}>
            {milestone.title}
            {milestone.dueDate ? ` — ${formatDate(milestone.dueDate)}` : ''}
            {milestone.isComplete ? ' (done)' : ''}
          </option>
        ))}
      </select>
    </Field>
  )
}
